import LicensingStoreService from '@/services/api/licensing-store/licensing-store-service';

const state = {
  products: [],
  activationQueue: [],
  activationQueueDetails: [],
  productsLoaded: false,
  contentLoaded: false,
  allowedLoaded: false,
  currentProductSKU: undefined,
};

const getters = {
  productsLoaded: state => state.productsLoaded,
  contentLoaded: state => state.contentLoaded && state.allowedLoaded,
  products: state => state.products,
  activationQueue: state => state.activationQueue,
  activationQueueDetails: state => state.activationQueueDetails,
  currentProductSKU: state => state.currentProductSKU,
  currentProduct: state => {
    if (!state.currentProductSKU || state.products.length === 0) {
      return null;
    }
    return state.products.find(p => p.sku === state.currentProductSKU);
  },
  isProductUnlimited: (state, getters) => {
    if (!getters.currentProduct) {
      return true;
    }
    return getters.currentProduct.pack === 'unlimited';
  },
  packContent: (state, getters) => {
    if (!getters.contentLoaded || !getters.currentProduct) {
      return ({
        ots: [],
        custom: [],
      });
    }
    return getters.currentProduct.content || {
      ots: [],
      custom: [],
    };
  },
  packContentCount: (state, getters) => {
    if (getters.packContent.ots) {
      return getters.packContent.ots.length;
    }
    return 0;
  },
  changesAllowed: (state, getters) => {
    if (!getters.contentLoaded || !getters.currentProduct) {
      return 0;
    }
    return getters.currentProduct.limit;
  },
  activationsAllowed: (state, getters) => {
    if (!getters.contentLoaded || !getters.currentProduct) {
      return 0;
    }
    const space = getters.currentProduct.pack - getters.packContentCount;
    return space < getters.changesAllowed ? space : getters.changesAllowed;
  },
};

const mutations = {
  setProducts(state, productsArray) {
    state.products = productsArray;
    state.productsLoaded = true;
  },
  setChangesAllowed(state, amount) {
    const prodIndex = state.products.findIndex(
      p => p.sku === state.currentProductSKU,
    );
    const newProducts = [...state.products];
    newProducts[prodIndex].limit = amount;
    state.products = newProducts;
    state.allowedLoaded = true;
  },
  setCurrentProductSKU(state, sku) {
    localStorage.setItem('managing-product', sku);
    state.currentProductSKU = sku;
  },
  setPackContent(state, contentResp) {
    const prodIndex = state.products.findIndex(
      p => p.sku === state.currentProductSKU,
    );
    const newProducts = [...state.products];
    newProducts[prodIndex].content = contentResp;
    state.products = newProducts;
    state.contentLoaded = true;
  },
  restartContentLoaded(state) {
    state.contentLoaded = false;
  },
  restartAllowedLoaded(state) {
    state.allowedLoaded = false;
  },
  setActivationQueue(state, val) {
    state.activationQueue = val;
  },
  setActivationQueueDetails(state, val) {
    state.activationQueueDetails = val;
  },
  removeActivationQueue(state) {
    state.activationQueue = [];
    state.activationQueueDetails = [];
  },
};

const actions = {
  getProducts({ commit, getters }) {
    const products = getters.products;
    if (products.length > 0) {
      return new Promise(resolve => resolve(products));
    }
    return LicensingStoreService.getProducts().then(res => {
      commit('setProducts', res);
      return res;
    });
  },
  getPackContent({ commit, getters }, params) {
    commit('restartContentLoaded');
    const content = getters.packContent;
    const sku = getters.currentProductSKU;
    if (content.length > 0) {
      return new Promise(resolve => resolve(content));
    }
    return LicensingStoreService.getContent(sku, params).then(res => {
      commit('setPackContent', res);
      return res;
    });
  },
  getChangesAllowed({ commit, getters }) {
    commit('restartAllowedLoaded');
    const allowed = getters.changesAllowed;
    const sku = getters.currentProductSKU;
    if (allowed > 0) {
      return new Promise(resolve => resolve(allowed));
    }
    return LicensingStoreService.getChangesAllowed(sku).then(res => {
      commit('setChangesAllowed', res.limit);
      return res.limit;
    });
  },
  activateContent({ commit, getters }, { data, isActivated, type }) {
    const sku = getters.currentProductSKU;
    const contents = data;
    const action = isActivated ? 'deactivateContent' : 'activateContent';
    const hasCodes = Object.values(contents).some(i => i.length);
    if (hasCodes) {
      return LicensingStoreService[action](sku, contents, type).then(res => {
        commit('setPackContent', res);
        if (res.limit) commit('setChangesAllowed', res.limit);
        commit('removeActivationQueue');
        res.action = action;
        return res;
      });
    }
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
